import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  ScrollText, Plus, Copy, Loader2, X, Check, Ban, AlertTriangle,
  Download, FileText, Mail, Send, Repeat, Save, Play, ZapOff, Shield,
  Server, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only page for generating and managing shareable Audit Mode links.
 * A fresh link is shown once with a copy button — after that only the first
 * 8 hex chars of the token are stored on the server as a safeguard against
 * accidental re-distribution.
 */
export default function AuditLinks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [just, setJust] = useState(null); // freshly-created link (shows URL once)

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/admin/audit-links")).data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (r) => {
    if (!window.confirm(`Revoke "${r.label}"? Anyone using this link will immediately lose access.`)) return;
    try {
      await api.delete(`/admin/audit-links/${r.id}`);
      toast.success("Link revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusOf = (r) => {
    if (r.revoked) return { label: "revoked", color: "bg-[#FAD7D7] text-[#B71C1C]" };
    const expired = new Date(r.expires_at) < new Date();
    if (expired) return { label: "expired", color: "bg-[#EEEBF5] text-[#5B3AA4]" };
    return { label: "active", color: "bg-[#DCF5D9] text-[#4A7C1E]" };
  };

  return (
    <div className="space-y-6" data-testid="audit-links-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="accp-label">Inspector tools</div>
          <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            <ScrollText size={28} className="inline mr-2 -mt-1" />
            Audit Mode links
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate time-limited read-only links for state inspectors. Revoke instantly if the audit ends early.
          </p>
        </div>
        <button onClick={() => setOpen(true)} className="accp-btn-primary" data-testid="new-audit-link-btn">
          <Plus size={14} /> Generate link
        </button>
      </div>

      <div className="rounded-xl bg-[#FFF8E6] border border-[#FFE4A0] text-[#7A5A00] p-4 text-sm">
        <AlertTriangle size={14} className="inline mr-1" />
        These links bypass authentication. Only share with authorised inspectors via a secure channel.
      </div>

      <PdfExportCard />
      <WeeklyDigestCard />
      <SecurityDigestCard />
      <EnvHealthCard />

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Code</th>
                <th className="p-4 accp-label">Label</th>
                <th className="p-4 accp-label">Created</th>
                <th className="p-4 accp-label">Expires</th>
                <th className="p-4 accp-label">Used</th>
                <th className="p-4 accp-label">Status</th>
                <th className="p-4 accp-label text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                  No audit links created yet.
                </td></tr>
              )}
              {rows.map((r) => {
                const st = statusOf(r);
                return (
                  <tr key={r.id} className="border-t border-border hover:bg-accent/20">
                    <td className="p-4 font-semibold text-primary">{r.code}</td>
                    <td className="p-4">{r.label}</td>
                    <td className="p-4 text-muted-foreground text-xs">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-muted-foreground text-xs">
                      {new Date(r.expires_at).toLocaleString()}
                    </td>
                    <td className="p-4">{r.used_count || 0}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${st.color}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {st.label === "active" && (
                        <button onClick={() => revoke(r)} className="accp-btn-ghost text-xs text-destructive"
                                data-testid={`revoke-${r.code}`}>
                          <Ban size={12} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && <NewLinkModal onClose={() => setOpen(false)} onCreated={(r) => {
        setOpen(false); setJust(r); load();
      }} />}
      {just && <CreatedLinkModal link={just} onClose={() => setJust(null)} />}
    </div>
  );
}

function NewLinkModal({ onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [hours, setHours] = useState(72);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/audit-links", { label: label.trim(), expires_hours: hours });
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="accp-modal-shell" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="accp-modal-card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>Generate link</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <label className="block">
          <span className="accp-label">Label — what is this link for?</span>
          <input className="accp-input mt-1" placeholder="State inspection April 2026"
                 value={label} onChange={(e) => setLabel(e.target.value)}
                 data-testid="audit-link-label" />
        </label>
        <label className="block">
          <span className="accp-label">Expires after</span>
          <select className="accp-input mt-1" value={hours} onChange={(e) => setHours(Number(e.target.value))}
                  data-testid="audit-link-hours">
            <option value={24}>24 hours</option>
            <option value={48}>48 hours</option>
            <option value={72}>72 hours</option>
            <option value={168}>7 days</option>
          </select>
        </label>
        <button onClick={submit} disabled={busy || !label.trim()}
                className="accp-btn-primary w-full" data-testid="generate-link">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Generate
        </button>
      </div>
    </div>
  );
}

function CreatedLinkModal({ link, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy — select the link manually");
    }
  };
  return (
    <div className="accp-modal-shell" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="accp-modal-card space-y-3"
           data-testid="created-link-modal">
        <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          Link generated
        </h3>
        <p className="text-xs text-muted-foreground">
          Copy it now. For security, the full URL will not be shown again.
        </p>
        <div className="rounded-xl bg-accent/40 p-3 text-xs break-all font-mono" data-testid="link-url">
          {link.url}
        </div>
        <button onClick={copy} className="accp-btn-primary w-full" data-testid="copy-link">
          <Copy size={14} /> Copy link
        </button>
      </div>
    </div>
  );
}

function PdfExportCard() {
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/audit-export/pdf", {
        params: { days },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `accp_audit_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit report downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="accp-card p-5 flex flex-wrap items-end gap-4" data-testid="audit-pdf-card">
        <div className="flex-1 min-w-[240px]">
          <div className="accp-label flex items-center gap-1">
            <FileText size={11} /> Audit Companion PDF
          </div>
          <h3 className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
            Download a branded audit report
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Self-contained PDF for in-person inspections. Includes compliance score, staff certifications,
            recent incidents, and the append-only audit log.
          </p>
        </div>
        <label className="block">
          <span className="accp-label">Period</span>
          <select
            className="accp-input mt-1 w-[140px]"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="audit-pdf-days"
            disabled={busy}
          >
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </label>
        <button
          onClick={() => setEmailOpen(true)}
          disabled={busy}
          className="accp-btn-ghost"
          data-testid="email-audit-pdf-btn"
        >
          <Mail size={14} /> Email PDF
        </button>
        <button
          onClick={download}
          disabled={busy}
          className="accp-btn-primary"
          data-testid="download-audit-pdf"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>
      {emailOpen && (
        <EmailPdfModal defaultDays={days} onClose={() => setEmailOpen(false)} />
      )}
    </>
  );
}

function EmailPdfModal({ defaultDays, onClose }) {
  const LAST_EMAIL_KEY = "accp_last_inspector_email";
  const [recipient, setRecipient] = useState(() => localStorage.getItem(LAST_EMAIL_KEY) || "");
  const [period, setPeriod] = useState(defaultDays);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = recipient.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/audit-export/email", {
        recipient_email: trimmed,
        period,
        note: note.trim() || null,
      });
      toast.success("Email is being sent", {
        description: `Queued for ${(r.data.recipients || []).join(", ")}`,
      });
      localStorage.setItem(LAST_EMAIL_KEY, trimmed);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="accp-modal-shell" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="accp-modal-card space-y-3"
           data-testid="email-pdf-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label flex items-center gap-1"><Mail size={11}/> Email audit PDF</div>
            <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              Send to inspector
            </h3>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <p className="text-xs text-muted-foreground">
          The report is generated asynchronously and delivered as a PDF attachment.
          Separate multiple recipients with commas (up to 5).
        </p>
        <label className="block">
          <span className="accp-label">Recipient email</span>
          <input
            className="accp-input mt-1"
            placeholder="inspector@maryland.gov, audit@agency.org"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            disabled={busy}
            data-testid="email-pdf-recipient"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="accp-label">Period</span>
          <select
            className="accp-input mt-1"
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            disabled={busy}
            data-testid="email-pdf-period"
          >
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </label>
        <label className="block">
          <span className="accp-label">Note to inspector (optional)</span>
          <textarea
            className="accp-input mt-1"
            rows={3}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            data-testid="email-pdf-note"
            placeholder="e.g. Requested during the April site inspection."
          />
        </label>
        <button
          onClick={submit}
          disabled={busy || !recipient.trim()}
          className="accp-btn-primary w-full"
          data-testid="email-pdf-send"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "Queuing…" : "Send email"}
        </button>
      </div>
    </div>
  );
}

function WeeklyDigestCard() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ enabled: false, recipients: "", period: 90 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [snoozes, setSnoozes] = useState([]);
  const [resumingEmail, setResumingEmail] = useState(null);

  const loadSnoozes = async () => {
    try {
      const r = await api.get("/admin/audit-digest/snoozes");
      setSnoozes(r.data || []);
    } catch {
      // Non-fatal — the main card still renders.
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit-digest/settings");
      setSettings(r.data);
      setDraft({
        enabled: !!r.data.enabled,
        recipients: (r.data.recipients || []).join(", "),
        period: r.data.period || 90,
      });
      await loadSnoozes();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = draft.recipients
        .split(",").map((s) => s.trim()).filter(Boolean);
      const r = await api.post("/admin/audit-digest/settings", {
        enabled: draft.enabled,
        recipients,
        period: draft.period,
      });
      setSettings(r.data);
      toast.success(draft.enabled ? "Weekly digest enabled" : "Weekly digest disabled");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the audit digest to all configured recipients now?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/audit-digest/send-now");
      if (r.data.ok) {
        toast.success("Digest sent", {
          description: `Delivered to ${(r.data.delivered_to || []).join(", ") || "—"}`,
        });
      } else {
        toast.warning(`Digest skipped: ${r.data.reason || "see logs"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const resume = async (email) => {
    if (!window.confirm(`Resume audit digest emails to ${email} immediately?`)) return;
    setResumingEmail(email);
    try {
      await api.post("/admin/audit-digest/resume", { email });
      toast.success(`Resumed ${email}`);
      loadSnoozes();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResumingEmail(null);
    }
  };

  if (loading) {
    return (
      <div className="accp-card p-5 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading digest settings…
      </div>
    );
  }

  const lastSent = settings?.last_sent_at
    ? new Date(settings.last_sent_at).toLocaleString()
    : "Never";
  const lastOk = settings?.last_result?.ok;

  return (
    <div className="accp-card p-5 space-y-4" data-testid="audit-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="accp-label flex items-center gap-1">
            <Repeat size={11} /> Weekly audit digest
          </div>
          <h3 className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
            Automated Monday-morning compliance report
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Ships the same branded PDF every Monday at 7:00 AM (server local time) to your configured
            distribution list. Toggle off any time — the job stays idle until re-enabled.
          </p>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer select-none"
               data-testid="digest-toggle-label">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
            className="sr-only peer"
            data-testid="digest-enabled"
          />
          <span className="relative inline-block w-11 h-6 bg-accent rounded-full transition
                           peer-checked:bg-primary">
            <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition
                             peer-checked:translate-x-5" />
          </span>
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">
            {draft.enabled ? "Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      <div className="grid md:grid-cols-[1fr_160px] gap-4">
        <label className="block">
          <span className="accp-label">Recipients (comma-separated, up to 5)</span>
          <input
            className="accp-input mt-1"
            placeholder="inspector@maryland.gov, qa@abelia.care"
            value={draft.recipients}
            onChange={(e) => setDraft((d) => ({ ...d, recipients: e.target.value }))}
            data-testid="digest-recipients"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="accp-label">Period</span>
          <select
            className="accp-input mt-1"
            value={draft.period}
            onChange={(e) => setDraft((d) => ({ ...d, period: Number(e.target.value) }))}
            data-testid="digest-period"
            disabled={saving}
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground" data-testid="digest-last-sent">
          Last sent:{" "}
          <b>{lastSent}</b>
          {settings?.last_result && (
            <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                              ${lastOk ? "bg-[#DCF5D9] text-[#4A7C1E]" : "bg-[#FAD7D7] text-[#B71C1C]"}`}>
              {lastOk ? "ok" : "issues"}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={sendNow}
            disabled={saving || sending || !settings?.enabled}
            className="accp-btn-ghost"
            data-testid="digest-send-now"
            title={!settings?.enabled ? "Enable and save the digest first" : "Send to all recipients now"}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send now
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="accp-btn-primary"
            data-testid="digest-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save settings
          </button>
        </div>
      </div>

      {snoozes.length > 0 && (
        <div className="pt-3 border-t border-border space-y-2" data-testid="active-snoozes">
          <div className="flex items-center gap-2">
            <ZapOff size={12} className="text-[#B8860B]" />
            <span className="accp-label">Active snoozes ({snoozes.length})</span>
          </div>
          <ul className="space-y-1.5">
            {snoozes.map((s) => (
              <li key={s.email}
                  className="flex items-center justify-between gap-3 rounded-lg bg-[#FFF8E6] border border-[#FFE4A0] px-3 py-2 text-xs"
                  data-testid={`snooze-row-${s.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#7A5A00] truncate">{s.email}</div>
                  <div className="text-[11px] text-[#7A5A00]/80">
                    paused until <b>{new Date(s.snoozed_until).toLocaleString()}</b>
                  </div>
                </div>
                <button
                  onClick={() => resume(s.email)}
                  disabled={resumingEmail === s.email}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white hover:bg-primary hover:text-white text-primary text-[11px] font-semibold border border-primary/30 transition disabled:opacity-50"
                  data-testid={`resume-${s.email}`}
                >
                  {resumingEmail === s.email
                    ? <Loader2 size={11} className="animate-spin" />
                    : <Play size={11} />}
                  Resume now
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}




/**
 * Iteration 19 — Weekly Security Digest card.
 * Minimal card for admins: "Send now" triggers POST /admin/security-digest/send-now,
 * shows loading state + summary counts in a success toast. No settings
 * storage in the DB yet — delivery list reuses CRITICAL_INCIDENT_RECIPIENTS.
 */
function SecurityDigestCard() {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const sendNow = async () => {
    if (!window.confirm(
      "Send the weekly security digest to the configured admin recipients now?",
    )) return;
    setSending(true);
    try {
      const r = await api.post("/admin/security-digest/send-now");
      setLastResult(r.data);
      if (r.data?.ok) {
        const s = r.data.summary || {};
        toast.success("Security digest sent", {
          description:
            `Logins: ${s.total_logins ?? 0} · Failed: ${s.failed_logins ?? 0} · ` +
            `New-device alerts: ${s.new_device_alerts ?? 0} · ` +
            `Revoked: ${s.sessions_revoked ?? 0}`,
        });
      } else {
        toast.warning("Digest skipped or delivery failed — see logs.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const s = lastResult?.summary || null;
  const gen = lastResult?.generated_at
    ? new Date(lastResult.generated_at).toLocaleString()
    : null;

  return (
    <div className="accp-card p-5 space-y-4" data-testid="security-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="accp-label flex items-center gap-1">
            <Shield size={11} /> Weekly security digest
          </div>
          <h3 className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
            7-day account-security summary
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Every Monday at 7:00 AM we email admins a digest of logins, failed
            attempts, password changes, new-device alerts and revocations from
            the trailing 7 days. Trigger a manual send below for pre-inspection
            checks or to verify delivery.
          </p>
        </div>
        <button
          onClick={sendNow}
          disabled={sending}
          className="accp-btn-primary disabled:opacity-60"
          data-testid="security-digest-send-now"
        >
          {sending
            ? <Loader2 size={14} className="animate-spin" />
            : <Send size={14} />}
          Send digest now
        </button>
      </div>

      {s && (
        <div className="rounded-xl bg-[#F5F3FA] ring-1 ring-border p-4"
             data-testid="security-digest-last-result">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
            Last run · {gen || "—"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Stat label="Successful logins" value={s.total_logins ?? 0} />
            <Stat label="Failed logins" value={s.failed_logins ?? 0}
                  warn={(s.failed_logins ?? 0) > 5} />
            <Stat label="Password changes" value={s.password_changes ?? 0} />
            <Stat label="New-device alerts" value={s.new_device_alerts ?? 0}
                  warn={(s.new_device_alerts ?? 0) > 3} />
            <Stat label="Sessions revoked" value={s.sessions_revoked ?? 0}
                  warn={(s.sessions_revoked ?? 0) > 2} />
            <Stat label="Alerts confirmed" value={s.new_device_confirmations ?? 0} />
            <Stat label="Alerts suppressed" value={s.new_device_suppressed ?? 0} />
            <Stat label="Delivered to"
                  value={(lastResult?.delivered_to || []).length} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn = false }) {
  return (
    <div className={`rounded-lg p-2.5 ring-1 ${
      warn ? "bg-[#FFF8E6] ring-[#FFE4A0]" : "bg-white ring-border"
    }`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`text-lg font-semibold mt-0.5 ${
        warn ? "text-[#8A5A00]" : "text-primary"
      }`}>
        {value}
      </div>
    </div>
  );
}

const ENV_CATEGORIES = {
  auth_seed: { label: "Auth & seed", icon: Shield },
  database:  { label: "Database", icon: Server },
  network:   { label: "Network", icon: Server },
  email:     { label: "Email", icon: Mail },
  push:      { label: "Push", icon: Send },
  misc:      { label: "Misc", icon: FileText },
};

function EnvHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/env-health");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const summary = data?.summary;
  const safe = summary?.production_safe;
  const grouped = (data?.vars || []).reduce((acc, v) => {
    (acc[v.category] = acc[v.category] || []).push(v);
    return acc;
  }, {});

  const renderValue = (v) => {
    if (!v.present) {
      return <span className="text-[11px] uppercase tracking-wider text-[#B71C1C]">unset</span>;
    }
    if (v.secret) {
      return (
        <span className="font-mono text-[11px] text-muted-foreground" title="Secret — only prefix shown">
          {v.prefix} <span className="text-[10px] text-muted-foreground/70">({v.length} chars)</span>
        </span>
      );
    }
    return <span className="font-mono text-[11px] text-foreground break-all">{v.value}</span>;
  };

  return (
    <div className="accp-card p-5 space-y-4" data-testid="env-health-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="accp-label flex items-center gap-1">
            <Server size={11} /> Deployment env health
          </div>
          <h3 className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
            Critical env vars (read-only probe)
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Verifies that the running container actually sees the env vars
            you set in the deployment panel. Secrets are reported by length
            and 4-char prefix only — full values are never returned.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="accp-btn-ghost disabled:opacity-60"
          data-testid="env-health-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {summary && (
        <div
          className={`rounded-xl p-4 ring-1 ${
            safe
              ? "bg-[#DCF5D9] ring-[#A0E08A] text-[#2C5C0E]"
              : "bg-[#FFF8E6] ring-[#FFE4A0] text-[#7A5A00]"
          }`}
          data-testid="env-health-banner"
        >
          <div className="flex items-center gap-2 text-sm font-semibold">
            {safe ? <Check size={16} /> : <AlertTriangle size={16} />}
            {safe
              ? "Production-safe: all P0 env vars set and SEED_DEMO_USERS=false"
              : "Action needed: one or more P0 env vars are misconfigured"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
            <SummaryDot label="Demo seed off"      ok={summary.seed_demo_users_safe} />
            <SummaryDot label="Admin email aligned" ok={summary.admin_email_aligned} />
            <SummaryDot label="Admin password set"  ok={summary.admin_password_set} />
            <SummaryDot label="JWT secret set"      ok={summary.jwt_secret_set} />
            <SummaryDot label="Mongo URL set"       ok={summary.mongo_url_set}
                        sub={summary.mongo_url_kind} />
            <SummaryDot label="DB name set"         ok={summary.db_name_set} />
          </div>
        </div>
      )}

      {Object.keys(grouped).length > 0 && (
        <div className="space-y-3">
          {Object.entries(ENV_CATEGORIES).map(([catKey, meta]) => {
            const rows = grouped[catKey] || [];
            if (rows.length === 0) return null;
            const Icon = meta.icon;
            return (
              <div key={catKey} className="rounded-xl ring-1 ring-border overflow-hidden">
                <div className="bg-[#F5F3FA] px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Icon size={11} /> {meta.label}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.name}
                          className="border-t first:border-t-0 border-border/40"
                          data-testid={`env-row-${v.name}`}>
                        <td className="px-4 py-2 font-mono text-[11px] text-foreground/80 w-1/3">
                          {v.name}
                        </td>
                        <td className="px-4 py-2">{renderValue(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryDot({ label, ok, sub }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-[#4A7C1E]" : "bg-[#B71C1C]"}`} />
      <span>{label}{sub ? <span className="text-muted-foreground"> · {sub}</span> : null}</span>
    </div>
  );
}
